import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import html2canvas from 'html2canvas';
import { MessageSquarePlus, X, Send, Check, Camera, Loader2 } from 'lucide-react';
import { apiService } from '@/services/api';
import { useI18n } from '@/contexts/i18n-hook';
import { cn } from '@/lib/utils';

// Gera um seletor CSS razoavelmente único para o elemento (id > caminho tag:nth-of-type)
function gerarSeletor(el: Element): string {
  const partes: string[] = [];
  let atual: Element | null = el;
  while (atual && atual !== document.body && partes.length < 6) {
    if (atual.id) { partes.unshift(`#${atual.id}`); break; }
    const tag = atual.tagName.toLowerCase();
    const pai: Element | null = atual.parentElement;
    if (!pai) { partes.unshift(tag); break; }
    const irmaos = Array.from(pai.children).filter((c) => c.tagName === atual!.tagName);
    partes.unshift(irmaos.length > 1 ? `${tag}:nth-of-type(${irmaos.indexOf(atual) + 1})` : tag);
    atual = pai;
  }
  return partes.join(' > ');
}

// Descrição legível do elemento: <button> "Salvar"
function descreverElemento(el: Element): string {
  const texto = (el.textContent || '').trim().replace(/\s+/g, ' ').slice(0, 80);
  return `<${el.tagName.toLowerCase()}>${texto ? ` "${texto}"` : ''}`;
}

interface Ret { left: number; top: number; width: number; height: number }

interface Selecao {
  rect: Ret;            // região do print, em coordenadas do viewport
  descricao: string;    // '<button> "Salvar"' ou o rótulo de área selecionada
  seletor: string | null;
  clickX: number;
  clickY: number;
}

const MARGEM_ELEMENTO = 24; // contexto ao redor do elemento no print
const LIMIAR_ARRASTO = 6;   // px até virar seleção de área

export function FeedbackWidget() {
  const { t } = useI18n();
  const [ativo, setAtivo] = useState(false);            // modo seleção ligado
  const [hoverRect, setHoverRect] = useState<DOMRect | null>(null);
  const [dragRect, setDragRect] = useState<Ret | null>(null); // retângulo sendo arrastado
  const [selecao, setSelecao] = useState<Selecao | null>(null); // seleção feita (popup aberto)
  const [screenshot, setScreenshot] = useState<string | null>(null);
  const [capturando, setCapturando] = useState(false);
  const [comentario, setComentario] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(false);
  const location = useLocation();
  const rootRef = useRef<HTMLDivElement>(null);
  const dragStart = useRef<{ x: number; y: number } | null>(null);

  const sair = useCallback(() => {
    setAtivo(false); setHoverRect(null); setDragRect(null);
    setSelecao(null); setScreenshot(null); setComentario('');
    dragStart.current = null;
  }, []);

  const cancelarPopup = useCallback(() => {
    setSelecao(null); setScreenshot(null); setComentario('');
  }, []);

  // Elementos do próprio widget não são selecionáveis nem entram no print
  const doWidget = useCallback((node: EventTarget | null) =>
    node instanceof Node && !!rootRef.current?.contains(node), []);

  // Tira o print de uma região do viewport (renderiza a página e recorta)
  const capturar = useCallback(async (rect: Ret) => {
    setCapturando(true);
    setScreenshot(null);
    try {
      const escala = Math.min(2, window.devicePixelRatio || 1);
      const canvas = await html2canvas(document.body, {
        scale: escala,
        useCORS: true,
        logging: false,
        backgroundColor: document.documentElement.classList.contains('dark') ? '#1A1A1A' : '#E5E5E5',
        ignoreElements: (el) => rootRef.current?.contains(el) ?? false,
      });
      // Recorta a região (coordenadas do documento = viewport + scroll da janela)
      const sx = (rect.left + window.scrollX) * escala;
      const sy = (rect.top + window.scrollY) * escala;
      const sw = rect.width * escala;
      const sh = rect.height * escala;
      const corte = document.createElement('canvas');
      corte.width = sw; corte.height = sh;
      corte.getContext('2d')!.drawImage(canvas, sx, sy, sw, sh, 0, 0, sw, sh);
      setScreenshot(corte.toDataURL('image/png'));
    } catch {
      setScreenshot(null); // segue sem print
    } finally {
      setCapturando(false);
    }
  }, []);

  // Fecha a seleção (clique em elemento ou área arrastada) e dispara o print
  const selecionar = useCallback((sel: Selecao) => {
    setSelecao(sel);
    setHoverRect(null);
    setDragRect(null);
    capturar(sel.rect);
  }, [capturar]);

  useEffect(() => {
    if (!ativo) return;

    const onMouseDown = (e: MouseEvent) => {
      if (doWidget(e.target) || selecao) return;
      e.preventDefault(); e.stopPropagation();
      dragStart.current = { x: e.clientX, y: e.clientY };
    };

    const onMove = (e: MouseEvent) => {
      if (selecao) return; // popup aberto: congela
      const inicio = dragStart.current;
      if (inicio && (Math.abs(e.clientX - inicio.x) > LIMIAR_ARRASTO || Math.abs(e.clientY - inicio.y) > LIMIAR_ARRASTO)) {
        // arrastando: retângulo de seleção
        setHoverRect(null);
        setDragRect({
          left: Math.min(inicio.x, e.clientX),
          top: Math.min(inicio.y, e.clientY),
          width: Math.abs(e.clientX - inicio.x),
          height: Math.abs(e.clientY - inicio.y),
        });
        return;
      }
      const el = document.elementFromPoint(e.clientX, e.clientY);
      if (!el || doWidget(el) || el === document.body || el === document.documentElement) {
        setHoverRect(null);
        return;
      }
      setHoverRect(el.getBoundingClientRect());
    };

    const onMouseUp = (e: MouseEvent) => {
      if (doWidget(e.target) || selecao) { dragStart.current = null; return; }
      e.preventDefault(); e.stopPropagation();
      const arrasto = dragRect;
      dragStart.current = null;

      if (arrasto && arrasto.width > 8 && arrasto.height > 8) {
        // Área desenhada com o mouse
        selecionar({
          rect: arrasto,
          descricao: t('layout.feedback.areaSelecionada'),
          seletor: null,
          clickX: arrasto.left + arrasto.width / 2,
          clickY: arrasto.top + arrasto.height,
        });
        return;
      }
      setDragRect(null);

      // Clique simples: seleciona o elemento sob o mouse
      const el = document.elementFromPoint(e.clientX, e.clientY);
      if (!el || el === document.body || el === document.documentElement) return;
      const r = el.getBoundingClientRect();
      selecionar({
        rect: {
          left: Math.max(0, r.left - MARGEM_ELEMENTO),
          top: Math.max(0, r.top - MARGEM_ELEMENTO),
          width: Math.min(window.innerWidth, r.right + MARGEM_ELEMENTO) - Math.max(0, r.left - MARGEM_ELEMENTO),
          height: Math.min(window.innerHeight, r.bottom + MARGEM_ELEMENTO) - Math.max(0, r.top - MARGEM_ELEMENTO),
        },
        descricao: descreverElemento(el),
        seletor: gerarSeletor(el),
        clickX: e.clientX,
        clickY: e.clientY,
      });
    };

    // Bloqueia cliques de acionarem botões/links da página no modo seleção
    const bloquear = (e: Event) => { if (!doWidget(e.target)) { e.preventDefault(); e.stopPropagation(); } };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') (selecao ? cancelarPopup() : sair()); };

    document.addEventListener('mousedown', onMouseDown, true);
    document.addEventListener('mousemove', onMove, true);
    document.addEventListener('mouseup', onMouseUp, true);
    document.addEventListener('click', bloquear, true);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', onMouseDown, true);
      document.removeEventListener('mousemove', onMove, true);
      document.removeEventListener('mouseup', onMouseUp, true);
      document.removeEventListener('click', bloquear, true);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [ativo, selecao, dragRect, doWidget, sair, cancelarPopup, selecionar, t]);

  // Trocar de página desliga o modo
  useEffect(() => { sair(); }, [location.pathname, sair]);

  // Cursor de mira enquanto seleciona
  useEffect(() => {
    document.body.style.cursor = ativo && !selecao ? 'crosshair' : '';
    return () => { document.body.style.cursor = ''; };
  }, [ativo, selecao]);

  const enviar = async () => {
    if (!selecao || !comentario.trim() || enviando || capturando) return;
    setEnviando(true);
    const res = await apiService.enviarFeedback({
      pagina: location.pathname,
      seletor: selecao.seletor,
      elemento: selecao.descricao,
      posX: selecao.clickX / window.innerWidth,
      posY: selecao.clickY / window.innerHeight,
      comentario: comentario.trim(),
      screenshot,
    });
    setEnviando(false);
    if (res.success) {
      sair();
      setEnviado(true);
      setTimeout(() => setEnviado(false), 2500);
    } else {
      alert(res.message || t('layout.feedback.erroEnviar'));
    }
  };

  // Posição do popup: perto da seleção, sem sair da tela
  const popupStyle = selecao ? {
    left: Math.max(8, Math.min(selecao.clickX, window.innerWidth - 350)),
    top: Math.max(8, Math.min(selecao.clickY + 12, window.innerHeight - 420)),
  } : undefined;

  const destaque: Ret | null = selecao?.rect ?? dragRect ?? (hoverRect && {
    left: hoverRect.left, top: hoverRect.top, width: hoverRect.width, height: hoverRect.height,
  });

  return (
    <div ref={rootRef}>
      {/* Destaque: elemento sob o mouse, retângulo arrastado ou seleção congelada */}
      {ativo && destaque && (
        <div
          className="pointer-events-none fixed z-[90] rounded border border-schmersal-yellow bg-schmersal-yellow/15"
          style={{ left: destaque.left - 2, top: destaque.top - 2, width: destaque.width + 4, height: destaque.height + 4 }}
        />
      )}

      {/* Faixa de instrução no topo */}
      {ativo && !selecao && (
        <div className="fixed left-1/2 top-3 z-[95] -translate-x-1/2 whitespace-nowrap rounded-full bg-schmersal-blue px-4 py-2 text-sm font-medium text-white shadow-lg">
          {t('layout.feedback.instrucao')} — <kbd className="rounded bg-white/20 px-1">Esc</kbd> {t('layout.feedback.instrucaoEsc')}
        </div>
      )}

      {/* Popup de comentário com prévia do print */}
      {selecao && (
        <div
          className="fixed z-[100] w-[340px] rounded-xl border border-schmersal-gray bg-white p-3 shadow-xl dark:border-schmersal-gray-dark dark:bg-schmersal-gray-darker"
          style={popupStyle}
        >
          <div className="mb-2 flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="text-sm font-bold text-schmersal-blue dark:text-white">{t('layout.feedback.titulo')}</div>
              <div className="truncate text-xs text-schmersal-gray-medium" title={selecao.descricao}>{selecao.descricao}</div>
            </div>
            <button onClick={cancelarPopup} className="rounded p-1 text-schmersal-gray-medium hover:bg-schmersal-gray-light dark:hover:bg-schmersal-gray-dark">
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Prévia do print */}
          <div className="mb-2 grid min-h-[90px] place-items-center overflow-hidden rounded-lg border border-schmersal-gray bg-schmersal-gray-light dark:border-schmersal-gray-dark dark:bg-schmersal-gray-darkest">
            {capturando ? (
              <div className="flex items-center gap-2 py-6 text-xs text-schmersal-gray-medium">
                <Loader2 className="h-4 w-4 animate-spin" /> {t('layout.feedback.gerandoPrint')}
              </div>
            ) : screenshot ? (
              <img src={screenshot} alt={t('layout.feedback.printAlt')} className="max-h-40 w-full object-contain" />
            ) : (
              <div className="flex items-center gap-2 py-6 text-xs text-schmersal-gray-medium">
                <Camera className="h-4 w-4" /> {t('layout.feedback.semPrint')}
              </div>
            )}
          </div>

          <textarea
            autoFocus
            value={comentario}
            onChange={(e) => setComentario(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) enviar(); }}
            placeholder={t('layout.feedback.comentarioPlaceholder')}
            rows={3}
            className="w-full resize-none rounded-lg border border-schmersal-gray bg-white p-2 text-sm text-schmersal-gray-dark focus:border-schmersal-blue focus:outline-none dark:border-schmersal-gray-dark dark:bg-schmersal-gray-darkest dark:text-white"
          />
          <div className="mt-2 flex justify-end gap-2">
            <button onClick={cancelarPopup} className="rounded-lg px-3 py-1.5 text-sm text-schmersal-gray-medium hover:bg-schmersal-gray-light dark:hover:bg-schmersal-gray-dark">
              {t('comum.acao.cancelar')}
            </button>
            <button
              onClick={enviar}
              disabled={!comentario.trim() || enviando || capturando}
              className="flex items-center gap-1.5 rounded-lg bg-schmersal-blue px-3 py-1.5 text-sm font-medium text-white hover:bg-schmersal-blue-light disabled:opacity-50"
            >
              <Send className="h-3.5 w-3.5" /> {enviando ? t('layout.feedback.enviando') : t('comum.acao.enviar')}
            </button>
          </div>
        </div>
      )}

      {/* Confirmação de envio */}
      {enviado && (
        <div className="fixed bottom-20 right-5 z-[95] flex items-center gap-2 rounded-lg bg-green-600 px-3 py-2 text-sm font-medium text-white shadow-lg">
          <Check className="h-4 w-4" /> {t('layout.feedback.enviado')}
        </div>
      )}

      {/* Botão flutuante */}
      <button
        onClick={() => (ativo ? sair() : setAtivo(true))}
        title={ativo ? t('layout.feedback.sairModo') : t('layout.feedback.abrirModo')}
        className={cn(
          'fixed bottom-5 right-5 z-[95] grid h-12 w-12 place-items-center rounded-full shadow-lg transition',
          ativo
            ? 'bg-schmersal-yellow text-schmersal-blue-dark'
            : 'bg-schmersal-blue text-white hover:bg-schmersal-blue-light dark:bg-schmersal-blue-light dark:hover:bg-schmersal-blue'
        )}
      >
        {ativo ? <X className="h-5 w-5" /> : <MessageSquarePlus className="h-5 w-5" />}
      </button>
    </div>
  );
}
